import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { ShieldCheck, Download, Wallet } from "lucide-react";
import HorizontalScrollContainer from "./HorizontalScrollContainer";

const money = (v) => {
  if (v === null || v === undefined) return "—";
  const raw = Number(v).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${raw.replace(/\u202f|\u00a0/g, " ")} €`;
};

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1)
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};

const frDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("fr-FR");
};

/**
 * Historique des relevés de rémunération validés (côté collaborateur).
 * Consultable et téléchargeable en PDF depuis « Mon profil » → « Ma rémunération ».
 */
export default function MyRemunerationStatements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/remuneration-statements/me/history");
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async (month) => {
    try {
      // /me/{month}/pdf n'existe pas — on utilise l'endpoint standard, le back
      // sait résoudre "self" via le token.
      const meRes = await api.get("/auth/me");
      const cid = meRes.data.id;
      const res = await api.get(`/remuneration-statements/${cid}/${month}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `remuneration_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'export PDF");
    }
  };

  return (
    <div className="mt-6 space-y-3" data-testid="my-remuneration-statements">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
            <ShieldCheck size={14} className="text-emerald-600" />
            Relevés de rémunération validés
          </h4>
          <p className="text-xs font-mono text-zinc-500">Consultez et téléchargez vos relevés mensuels validés par la direction.</p>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 text-center font-mono text-sm text-zinc-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-8 text-center" data-testid="my-remuneration-empty">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-zinc-100 mb-2">
            <Wallet size={18} className="text-zinc-500" />
          </div>
          <p className="text-sm text-zinc-700">Aucun relevé validé pour le moment.</p>
          <p className="text-xs font-mono text-zinc-500 mt-1">Les relevés apparaîtront dès qu&apos;ils auront été validés par l&apos;administration.</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <HorizontalScrollContainer testId="my-statements-scroll">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="text-zinc-500 text-[10px] uppercase tracking-wider bg-zinc-50">
                <tr>
                  <th className="text-left px-4 py-2">Période</th>
                  <th className="text-right px-4 py-2">Fixe</th>
                  <th className="text-right px-4 py-2">Unitaire</th>
                  <th className="text-right px-4 py-2">Taux %</th>
                  <th className="text-right px-4 py-2">Total HT</th>
                  <th className="text-right px-4 py-2">Validé le</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-zinc-100" data-testid={`my-statement-row-${it.month}`}>
                    <td className="px-4 py-2.5 font-medium text-zinc-900 capitalize">{monthLabel(it.month)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{money(it.fixed_amount_prorated)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{money(it.variable_unit_total)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{money(it.variable_rate_amount)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">{money(it.total_ht)}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-mono text-zinc-500">{frDate(it.validated_at)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(it.month)} data-testid={`my-statement-pdf-${it.month}`}>
                        <Download size={12} className="mr-1" /> PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HorizontalScrollContainer>
        </div>
      )}
    </div>
  );
}
